import { useEffect, useRef, useState, useMemo } from "react";
import {
  Grid,
  Typography,
  Box,
  Button,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
} from "@mui/material";
import { FileDownload } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import * as XLSX from "xlsx";
import { fitColumns } from "../../utils/excelFit";
import Layout from "../../layout/Layout";
import { fetchSalesCallAPI } from "../../config";
import { useNavigate, useLocation } from "react-router-dom";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import { MaterialReactTable } from "material-react-table";
import { apiData } from "../../services/api_service";
import dayjs from "dayjs";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { dateColumnFilterProps } from "../../components/MrtDateColumnFilter";
import { buildExportFileName } from "../../utils/exportFileName";
import { writeXlsxFrozen } from "../../utils/xlsxFreeze";

// Distinct color per Customer Type so the list scans quickly without
// reading every cell — same "chip, not plain text" pattern used for status
// columns elsewhere in the app.
const CUSTOMER_TYPE_COLORS = {
  Company: { bg: "#e3f2fd", color: "#0c54a0" },
  Individual: { bg: "#f3e5f5", color: "#7b1fa2" },
  Coach: { bg: "#e8f5e9", color: "#2e7d32" },
  "Academy Owner": { bg: "#fff3e0", color: "#e65100" },
  "Admin / Manager": { bg: "#fce4ec", color: "#ad1457" },
};

function SalesCallList() {
  const navigate = useNavigate();

  const [totalCount, setTotalCount] = useState(0);
  const [callList, setCallList] = useState([]);
  // Admin / Chief Executive see everyone's logs; others see only their own —
  // so the "Created By" column is only meaningful for managers.
  const [isManager, setIsManager] = useState(false);

  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const location = useLocation();

  const [customFromDate, setCustomFromDate] = useState(() => {
    return location.state?.fromDate || null;
  });
  const [customToDate, setCustomToDate] = useState(() => {
    return location.state?.toDate || null;
  });

  // Calendar month ("YYYY-MM") + week-of-month. Defaults to current month/week
  // unless custom dates are passed from dashboard.
  const [monthValue, setMonthValue] = useState(() => {
    if (location.state?.fromDate) {
      return dayjs(location.state.fromDate).format("YYYY-MM");
    }
    return dayjs().format("YYYY-MM");
  });
  const [weekValue, setWeekValue] = useState(() => {
    if (location.state?.fromDate) {
      return "all";
    }
    return "all";
  });

  // Map month + week to an inclusive date range.
  const monthRange = useMemo(() => {
    if (customFromDate && customToDate) {
      return { fromDate: customFromDate, toDate: customToDate };
    }
    if (!monthValue) return { fromDate: null, toDate: null };
    const monthStart = dayjs(monthValue + "-01").startOf("month");
    const monthEnd = monthStart.endOf("month");
    if (weekValue === "all") {
      return {
        fromDate: monthStart.format("YYYY-MM-DD"),
        toDate: monthEnd.format("YYYY-MM-DD"),
      };
    }
    const from = monthStart.add((parseInt(weekValue, 10) - 1) * 7, "day");
    if (from.isAfter(monthEnd)) return { fromDate: null, toDate: null };
    const to = from.add(6, "day");
    return {
      fromDate: from.format("YYYY-MM-DD"),
      toDate: (to.isAfter(monthEnd) ? monthEnd : to).format("YYYY-MM-DD"),
    };
  }, [monthValue, weekValue, customFromDate, customToDate]);

  // Export the current view (respects the month/week selection + filters).
  const handleExportCalls = async () => {
    try {
      setLoading(true);
      const payload = {
        pageIndex: 0,
        pageSize: 1000000,
        sorting: sorting.length > 0 ? JSON.stringify(sorting[0]) : null,
        search: filters.length > 0 ? JSON.stringify(filters) : null,
        fromDate: monthRange.fromDate,
        toDate: monthRange.toDate,
        sales_person_id: location.state?.salesPersonId || undefined,
      };
      const response = await apiData("get", fetchSalesCallAPI, payload);
      const data = response?.data?.result || [];
      if (!data.length) {
        alert("No data to export!");
        return;
      }
      const exportData = data.map((item, index) => ({
        "#": index + 1,
        "Sales Executive": item.created_by_name || "-",
        "Call Date": item.call_date
          ? dayjs(item.call_date).format("DD-MM-YYYY")
          : "-",
        "Follow-up Date": item.followup_date
          ? dayjs(item.followup_date).format("DD-MM-YYYY")
          : "-",
        "Company Name": item.company_name || "-",
        "Customer / Store / School": item.customer_entity_name || "-",
        "Customer Type": item.customer_type || "-",
        Location: item.location || "-",
        Phone: item.contact_phone || "-",
        Email: item.contact_email || "-",
        Remarks: item.remarks || "-",
        "Expected Business":
          item.expected_business !== null && item.expected_business !== undefined && item.expected_business !== ""
            ? isNaN(Number(item.expected_business))
              ? item.expected_business
              : Number(item.expected_business)
            : "",
        "Created At": item.created_at
          ? dayjs(item.created_at).format("DD-MM-YYYY")
          : "-",
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      fitColumns(ws);
      XLSX.utils.book_append_sheet(wb, ws, "SalesCalls");
      writeXlsxFrozen(XLSX, 
        wb,
        buildExportFileName("Sales_Call_Report", [
          location.state?.salesPersonName
            ? { label: "Sales_Executive", value: location.state.salesPersonName }
            : location.state?.salesPersonId
            ? { label: "Sales_Executive", value: location.state.salesPersonId }
            : { label: "Sales_Executive", value: "All" },
          monthRange.fromDate
            ? { label: "From", value: dayjs(monthRange.fromDate).format("DD-MM-YYYY") }
            : null,
          monthRange.toDate
            ? { label: "To", value: dayjs(monthRange.toDate).format("DD-MM-YYYY") }
            : null,
        ])
      );
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  const tempRefs = useRef(false);
  useEffect(() => {
    if (tempRefs.current === false) {
      fetchCalls();
    }
  }, []);

  const fetchCalls = async () => {
    setLoading(true);
    const payload = {
      pageIndex,
      pageSize,
      sorting: sorting.length > 0 ? JSON.stringify(sorting[0]) : null,
      search: filters.length > 0 ? JSON.stringify(filters) : null,
      fromDate: monthRange.fromDate,
      toDate: monthRange.toDate,
      sales_person_id: location.state?.salesPersonId || undefined,
    };
    try {
      const response = await apiData("get", fetchSalesCallAPI, payload);
      setCallList(response.data.result || []);
      setTotalCount(response.data.totalCount || 0);
      setIsManager(Boolean(response.data.isManager));
    } catch (error) {
      console.error("Error fetching sales call reports:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageIndex,
    pageSize,
    filters,
    sorting,
    monthRange.fromDate,
    monthRange.toDate,
  ]);

  const columns = useMemo(
    () => [
      {
        // Sales executives can edit only on the day the report was created;
        // Admin can always edit. The server decides (can_edit) and enforces it.
        id: "actions",
        header: "Edit",
        size: 60,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) =>
          row.original.can_edit ? (
            <Tooltip title="Edit">
              <IconButton
                size="small"
                color="primary"
                onClick={() => navigate(`/sales-call-report/edit/${row.original.id}`)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Locked — reports can only be edited on the day they were created">
              <LockOutlinedIcon fontSize="small" color="disabled" />
            </Tooltip>
          ),
      },
      {
        accessorKey: "created_by_name",
        header: "Sales Executive",
        size: 140,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "call_date",
        header: "Call Date",
        size: 100,
        ...dateColumnFilterProps(),
        Cell: ({ cell }) =>
          cell.getValue()
            ? dayjs(cell.getValue()).format("DD-MM-YYYY")
            : "-",
      },
      {
        accessorKey: "followup_date",
        header: "Follow-up Date",
        size: 110,
        ...dateColumnFilterProps(),
        // A follow-up due today or already overdue is the one thing on this
        // page a sales exec/manager actually needs to act on — flag it
        // rather than leaving every date looking equally routine.
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return "-";
          const isDue = dayjs(value).isBefore(dayjs().add(1, "day"), "day");
          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: isDue ? 700 : 400,
                color: isDue ? "#c62828" : "inherit",
              }}
            >
              {dayjs(value).format("DD-MM-YYYY")}
            </Typography>
          );
        },
      },
      {
        accessorKey: "company_name",
        header: "Company Name",
        size: 160,
        Cell: ({ row }) => <strong>{row.original.company_name || "-"}</strong>,
      },
      {
        accessorKey: "customer_entity_name",
        header: "Customer / Store / School",
        size: 180,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "customer_type",
        header: "Customer Type",
        size: 140,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return "-";
          const palette = CUSTOMER_TYPE_COLORS[value] || {
            bg: "#eceff1",
            color: "#455a64",
          };
          return (
            <Chip
              label={value}
              size="small"
              data-lmggt-handled="true"
              sx={{
                bgcolor: palette.bg,
                color: palette.color,
                fontWeight: 600,
              }}
            />
          );
        },
      },
      {
        accessorKey: "location",
        header: "Location",
        size: 130,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      { accessorKey: "contact_phone", header: "Phone", size: 120 },
      { accessorKey: "contact_email", header: "Email", size: 160 },
      {
        // Plain text, no whiteSpace:"pre-line" wrapper — that rendered
        // multi-line block content the global row-height clamp (see
        // tableEnhancements.css §2) can't cleanly clip, so long remarks
        // (some of these run to several paragraphs) blew out the row
        // height inconsistently with every other column. Letting it fall
        // through as plain text gives the same single-line-with-ellipsis
        // clamp + click-to-expand-in-a-dialog behavior every other cell
        // on this page already gets for free.
        accessorKey: "remarks",
        header: "Remarks",
        size: 220,
      },
      {
        accessorKey: "expected_business",
        header: "Expected Business",
        size: 200,
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        size: 110,
        ...dateColumnFilterProps(),
        Cell: ({ cell }) => cell.getValue() ? dayjs(cell.getValue()).format("DD-MM-YYYY") : "-",
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isManager]
  );

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              {location.state?.reportTitle || location.state?.title || "Sales Call Reports"}
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Sales Call Reports</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid
            textAlign="right"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <LoadingButton
              size="medium"
              variant="contained"
              color="success"
              className="button_first"
              href="add"
              startIcon={<AddIcon />}
            >
              Log Sales Call
            </LoadingButton>
            <LoadingButton
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>

        <Grid className="form_design">
          <MaterialReactTable
            columns={columns}
            data={callList}
            enableGlobalFilter={false}
            enableFullScreenToggle={false}
            enableStickyHeader
            className="mat-react-tbl"
            layoutMode="grid"
            renderTopToolbarCustomActions={() => (
              <Box
                sx={{
                  p: "0.5rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Tooltip title="Export data to excel sheet" arrow>
                  <Button
                    onClick={handleExportCalls}
                    style={{ background: "#1a73e829" }}
                    startIcon={<FileDownload />}
                  >
                    Export To Excel
                  </Button>
                </Tooltip>

                {/* Calendar month + week-of-month — defaults to the current
                    month/week; Clear to see all records. Uses year+month views
                    so the year can be switched (the native month input hides
                    year navigation). */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MobileDatePicker
                    views={["year", "month"]}
                    openTo="month"
                    label="Month"
                    value={monthValue ? dayjs(monthValue + "-01") : null}
                    onChange={(d) => {
                      setCustomFromDate(null);
                      setCustomToDate(null);
                      setMonthValue(d ? dayjs(d).format("YYYY-MM") : "");
                      setPageIndex(0);
                    }}
                    inputFormat="MMM YYYY"
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ width: 170 }} />
                    )}
                  />
                </LocalizationProvider>
                <FormControl
                  size="small"
                  sx={{ minWidth: 150 }}
                  disabled={!monthValue}
                >
                  <InputLabel>Week</InputLabel>
                  <Select
                    label="Week"
                    value={weekValue}
                    onChange={(e) => {
                      setCustomFromDate(null);
                      setCustomToDate(null);
                      setWeekValue(e.target.value);
                      setPageIndex(0);
                    }}
                  >
                    <MenuItem value="all">Whole month</MenuItem>
                    <MenuItem value="1">Week 1 (1–7)</MenuItem>
                    <MenuItem value="2">Week 2 (8–14)</MenuItem>
                    <MenuItem value="3">Week 3 (15–21)</MenuItem>
                    <MenuItem value="4">Week 4 (22–28)</MenuItem>
                    <MenuItem value="5">Week 5 (29–end)</MenuItem>
                  </Select>
                </FormControl>
                {(monthValue || customFromDate) && (
                  <Button
                    size="small"
                    onClick={() => {
                      setCustomFromDate(null);
                      setCustomToDate(null);
                      setMonthValue("");
                      setWeekValue("all");
                      setPageIndex(0);
                    }}
                  >
                    Clear
                  </Button>
                )}

                <Typography variant="caption" color="text.secondary">
                  {monthValue
                    ? `Showing ${dayjs(monthValue + "-01").format("MMM YYYY")}${
                        weekValue !== "all" ? ", week " + weekValue : ""
                      }`
                    : "Showing all records"}{" "}
                  · {totalCount} record{totalCount === 1 ? "" : "s"}
                </Typography>
              </Box>
            )}
            state={{
              isLoading: loading,
              pagination: { pageIndex, pageSize },
              sorting,
              columnFilters: filters,
            }}
            manualPagination
            enableRowNumbers
            manualSorting
            manualFiltering
            rowCount={totalCount}
            onPaginationChange={(updater) => {
              const paginationState = updater({ pageIndex, pageSize });
              const { pageIndex: newPageIndex, pageSize: newPageSize } =
                paginationState;
              setPageIndex(newPageIndex);
              setPageSize(newPageSize);
            }}
            onSortingChange={setSorting}
            onColumnFiltersChange={setFilters}
            muiTableContainerProps={{
              sx: {
                maxHeight: "calc(100vh - 240px)",
                minHeight: 450,
                overflowY: "auto",
              },
            }}
          />
        </Grid>
      </Grid>
    </Layout>
  );
}

export default SalesCallList;
